import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FileText, Download, Send, Truck, Package, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import axios from "axios";
import getApiUrl from "@/utils/api";

const API = getApiUrl();

export default function BillOfLading({ bookingData, onComplete }) {
  const formRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Auto-build items description from booking data
  const buildItemsDesc = () => {
    if (!bookingData) return "";
    const parts = [];
    const barrels = bookingData.total_barrels || 0;
    const suitcases = bookingData.total_suitcases || 0;
    const carryOns = bookingData.total_carry_ons || 0;
    if (barrels > 0) parts.push(`${barrels} Barrel${barrels > 1 ? "s" : ""}`);
    if (suitcases > 0) parts.push(`${suitcases} Suitcase${suitcases > 1 ? "s" : ""}`);
    if (carryOns > 0) parts.push(`${carryOns} Carry-on${carryOns > 1 ? "s" : ""}`);
    return parts.join(", ");
  };

  const totalPieces = () => {
    if (!bookingData) return "";
    return (bookingData.total_barrels || 0) + (bookingData.total_suitcases || 0) + (bookingData.total_carry_ons || 0);
  };

  const [formData, setFormData] = useState({
    shipFromName: bookingData?.full_name || "",
    shipFromAddress: bookingData?.housing_address || bookingData?.housing_unit || "",
    shipFromCityStateZip: "Wisconsin Dells, WI 53965",
    shipFromPhone: bookingData?.whatsapp_number || "",
    // Receiver / customs pickup
    receiverName: "",
    receiverAddress: "",
    receiverCityStateZip: "",
    receiverPhone1: "",
    receiverPhone2: "",
    // Shipment details
    date: bookingData?.travel_date || new Date().toISOString().split("T")[0],
    bolNumber: `TM-${Date.now().toString().slice(-8)}`,
    itemsDescription: buildItemsDesc(),
    numberOfPieces: String(totalPieces() || ""),
    weight: "",
    specialInstructions: `Flight: ${bookingData?.airline || ""} ${bookingData?.flight_number || ""}\nTravel Date: ${bookingData?.travel_date || ""}\nDeparture: ${bookingData?.departure_time || bookingData?.travel_time || ""}`,
    shipperSignature: "",
    shipperDate: new Date().toISOString().split("T")[0],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrint = () => window.print();

  const handleDownloadPDF = () => {
    window.open("https://customer-assets.emergentagent.com/job_99515ecf-0f57-43af-8b37-430c3fa3784d/artifacts/hwqavab7_T%20%26%20M%20Courier%20Bill-of-lading.pdf", "_blank");
    toast.info("PDF opened. Fill in your details and save.");
  };

  const handleSubmit = async () => {
    if (!formData.shipFromName || !formData.receiverName || !formData.receiverPhone1) {
      toast.error("Please fill in sender name, receiver name, and at least one receiver phone number");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/shipping/bill-of-lading`, {
        booking_id: bookingData?.id || "",
        booking_confirmation: bookingData?.confirmation_number || "",
        ...formData,
      });
      setSaved(true);
      toast.success("Bill of Lading submitted successfully!");
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit Bill of Lading");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          .bol-card { box-shadow: none !important; border-radius: 0 !important; max-width: 100% !important; }
          .bol-card input, .bol-card textarea { border: 1px solid #ccc !important; font-size: 10pt !important; padding: 2px 4px !important; min-height: auto !important; height: auto !important; }
          .bol-card label { font-size: 8pt !important; }
          .bol-card .space-y-6 { gap: 0.5rem !important; }
          .bol-card .p-6 { padding: 0.4in !important; }
        }
        @page { margin: 0.3in; size: letter; }
      `}</style>
    <Card className="bol-card shadow-xl border-0 max-w-3xl mx-auto print:shadow-none" ref={formRef}>
      <CardHeader className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-t-lg print:bg-orange-500">
        <CardTitle className="flex items-center gap-2">
          <Truck className="w-6 h-6" />
          T&M Courier Services - Bill of Lading
        </CardTitle>
        <p className="text-white/80 text-sm">
          (815) 914-8139 | Tmcourierservice1@gmail.com
        </p>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* Header Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Date</Label>
            <Input name="date" type="date" value={formData.date} onChange={handleInputChange} className="mt-1" data-testid="bol-date" />
          </div>
          <div>
            <Label>Bill of Lading #</Label>
            <Input name="bolNumber" value={formData.bolNumber} className="mt-1 font-mono" readOnly data-testid="bol-number" />
          </div>
        </div>

        {/* Ship From */}
        <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
          <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
            <Package className="w-4 h-4" />
            SHIP FROM (Your Wisconsin Address)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Name *</Label>
              <Input name="shipFromName" value={formData.shipFromName} onChange={handleInputChange} placeholder="Your full name" className="mt-1" data-testid="bol-from-name" />
            </div>
            <div>
              <Label className="text-sm">Phone *</Label>
              <Input name="shipFromPhone" value={formData.shipFromPhone} onChange={handleInputChange} placeholder="WhatsApp number" className="mt-1" data-testid="bol-from-phone" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Address *</Label>
              <AddressAutocomplete value={formData.shipFromAddress} onChange={(val) => setFormData(prev => ({ ...prev, shipFromAddress: val }))} placeholder="Start typing housing address..." className="mt-1" dataTestId="bol-ship-from-address" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">City, State, Zip *</Label>
              <AddressAutocomplete value={formData.shipFromCityStateZip} onChange={(val) => setFormData(prev => ({ ...prev, shipFromCityStateZip: val }))} placeholder="City, State ZIP" className="mt-1" dataTestId="bol-ship-from-csz" />
            </div>
          </div>
        </div>

        {/* Receiver / Customs Pickup */}
        <div className="p-4 bg-emerald-50 rounded-xl border-2 border-emerald-300">
          <h3 className="font-bold text-emerald-800 mb-1 flex items-center gap-2">
            <Send className="w-4 h-4" />
            RECEIVER / CUSTOMS PICKUP
          </h3>
          <p className="text-xs text-emerald-700 mb-3">Who will pick up the items once they clear customs? Provide 2 phone numbers.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-sm">Receiver Full Name *</Label>
              <Input name="receiverName" value={formData.receiverName} onChange={handleInputChange} placeholder="Full name of person picking up at customs" className="mt-1" data-testid="bol-receiver-name" />
            </div>
            <div>
              <Label className="text-sm">Phone 1 (Primary) *</Label>
              <Input name="receiverPhone1" value={formData.receiverPhone1} onChange={handleInputChange} placeholder="+1 (xxx) xxx-xxxx" className="mt-1" data-testid="bol-receiver-phone1" />
            </div>
            <div>
              <Label className="text-sm">Phone 2 (Backup) *</Label>
              <Input name="receiverPhone2" value={formData.receiverPhone2} onChange={handleInputChange} placeholder="+1 (xxx) xxx-xxxx" className="mt-1" data-testid="bol-receiver-phone2" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">Receiver Address *</Label>
              <AddressAutocomplete value={formData.receiverAddress} onChange={(val) => setFormData(prev => ({ ...prev, receiverAddress: val }))} placeholder="Delivery address after customs clearance" className="mt-1" dataTestId="bol-receiver-address" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sm">City, State/Parish, Zip *</Label>
              <Input name="receiverCityStateZip" value={formData.receiverCityStateZip} onChange={handleInputChange} placeholder="e.g., Kingston, St. Andrew, Jamaica" className="mt-1" data-testid="bol-receiver-csz" />
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
          <h3 className="font-bold text-yellow-800 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            ITEMS TO SHIP
          </h3>
          {bookingData && (bookingData.total_barrels > 0 || bookingData.total_suitcases > 0) && (
            <div className="mb-3 p-2 bg-white rounded-lg border border-yellow-200 text-xs">
              <span className="font-semibold text-yellow-800">Auto-filled from your booking: </span>
              <span className="text-yellow-700">{buildItemsDesc()}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-3">
              <Label className="text-sm">Description of Items *</Label>
              <Textarea name="itemsDescription" value={formData.itemsDescription} onChange={handleInputChange} placeholder="e.g., 2 Barrels, 1 Scooter, 1 TV (32 inch), 3 Extra Suitcases" className="mt-1" rows={3} data-testid="bol-items-desc" />
            </div>
            <div>
              <Label className="text-sm">Number of Pieces</Label>
              <Input name="numberOfPieces" value={formData.numberOfPieces} onChange={handleInputChange} placeholder="e.g., 5" className="mt-1" data-testid="bol-pieces" />
            </div>
            <div>
              <Label className="text-sm">Total Weight (lbs)</Label>
              <Input name="weight" value={formData.weight} onChange={handleInputChange} placeholder="e.g., 150" className="mt-1" data-testid="bol-weight" />
            </div>
          </div>
          <div className="mt-3 text-xs text-yellow-700">
            <p className="font-medium">You can ship:</p>
            <div className="flex flex-wrap gap-1 mt-1">
              {["Barrels", "Extra Suitcases", "Scooters", "Bicycles", "TVs", "Large/Heavy Items"].map(item => (
                <span key={item} className="px-2 py-0.5 bg-white rounded-full border border-yellow-300">{item}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        <div>
          <Label>Special Instructions / Flight Info</Label>
          <Textarea name="specialInstructions" value={formData.specialInstructions} onChange={handleInputChange} className="mt-1" rows={3} data-testid="bol-special" />
        </div>

        {/* Signature */}
        <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-3">SHIPPER SIGNATURE</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Signature (Type your full name) *</Label>
              <Input name="shipperSignature" value={formData.shipperSignature} onChange={handleInputChange} placeholder="Type your full legal name" className="mt-1 italic" style={{ fontFamily: "cursive" }} data-testid="bol-signature" />
              <p className="text-xs text-muted-foreground mt-1">By typing your name, you certify the contents are properly described and packaged.</p>
            </div>
            <div>
              <Label className="text-sm">Date</Label>
              <Input name="shipperDate" type="date" value={formData.shipperDate} onChange={handleInputChange} className="mt-1" data-testid="bol-sign-date" />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 no-print">
          <Button onClick={handleSubmit} disabled={saving || saved} className={`flex-1 ${saved ? "bg-emerald-600" : "bg-orange-600 hover:bg-orange-700"}`} data-testid="bol-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : saved ? <CheckCircle className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {saved ? "Submitted" : saving ? "Submitting..." : "Submit Bill of Lading"}
          </Button>
          <Button onClick={handlePrint} variant="outline" className="flex-1">
            <FileText className="w-4 h-4 mr-2" /> Print This Form
          </Button>
          <Button onClick={handleDownloadPDF} variant="outline" className="flex-1">
            <Download className="w-4 h-4 mr-2" /> Download Original PDF
          </Button>
        </div>

        <div className="text-center text-sm text-muted-foreground pt-4 border-t no-print">
          <p className="font-medium">Questions about shipping?</p>
          <p>Call T&M Courier: (815) 914-8139</p>
          <p>Email: Tmcourierservice1@gmail.com</p>
        </div>
      </CardContent>
    </Card>
    </>
  );
}
